import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import styles from './ExpandableText.module.css';

type Props = {
  /** Number of visible text lines while collapsed. */
  readonly lines: number;
  /** Localized label for the expand control, e.g. "…more". The label carries the ellipsis — the clamp cuts at a line boundary without drawing one. */
  readonly moreLabel: string;
  readonly children: ReactNode;
};

// LinkedIn-style collapsed preview: cuts content after `lines` text lines
// and shows an "…more" control directly under the text when the content
// actually overflows. Expansion is one-way — once opened the content stays
// open and renders unwrapped.
//
// The cut is a measured max-height rather than -webkit-line-clamp: the
// native clamp ellipsis can't be disabled, competes with the control's own,
// and only works on a single block — post bodies render markdown with
// multiple <p> blocks separated by margins. For the same reason the cut
// height cannot be `lines × line-height`: block margins, list padding, and
// oversized glyph boxes all shift where lines actually sit. Instead the
// measurement walks the rendered text and takes the bottom edge of the Nth
// visual line from its client rects, so the cut always lands exactly on a
// line boundary regardless of what block structure produced the lines.
// Lines counted are ones containing visible text — a pre-wrap paragraph gap
// or block margin is never the last thing above the control.
export function ExpandableText({ lines, moreLabel, children }: Props) {
  const clampRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [cutHeight, setCutHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = clampRef.current;
    if (el === null) return;
    const measure = () => {
      // maxHeight only clips paint — the content keeps its natural layout,
      // so rects measured while clamped match the unclamped geometry.
      const bottoms = textLineBottoms(el);
      setCutHeight(
        bottoms.length > lines
          ? Math.ceil(bottoms[lines - 1]! - el.getBoundingClientRect().top)
          : null,
      );
    };
    measure();
    // Web fonts load with font-display: swap — the swap re-wraps lines
    // without changing the element's width, so the ResizeObserver below
    // never sees it. One extra measure after fonts settle covers it.
    let cancelled = false;
    void el.ownerDocument.fonts.ready.then(() => {
      if (!cancelled) measure();
    });
    // Re-measure on width changes — line wrapping shifts where the cut
    // bites. Height-only resizes are ignored: the cut changes our own
    // height, and reacting to that would loop.
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      measure();
    });
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [expanded, lines, children]);

  if (expanded) return <>{children}</>;

  return (
    <div>
      <div
        ref={clampRef}
        className={styles.clamp}
        style={cutHeight !== null ? { maxHeight: cutHeight } : undefined}
      >
        {children}
      </div>
      {cutHeight !== null ? (
        <button
          type="button"
          className={styles.more}
          aria-expanded={false}
          onClick={() => setExpanded(true)}
        >
          {moreLabel}
        </button>
      ) : null}
    </div>
  );
}

// Bottom edges (viewport coords) of each visual line containing text, in
// document order. A range over a text node yields one client rect per line
// fragment; fragments from sibling inline nodes (bold runs, mention links)
// land on the same line and are merged by top proximity. Zero-width rects
// (the caret rect of a pre-wrap newline, collapsed whitespace) are dropped so
// blank lines don't count.
function textLineBottoms(el: HTMLElement): number[] {
  const doc = el.ownerDocument;
  const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = doc.createRange();
  const fragments: Array<{ top: number; bottom: number; height: number }> = [];
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    const text = node as Text;
    if (text.data.trim().length === 0) continue;
    range.selectNodeContents(text);
    for (const rect of range.getClientRects()) {
      if (rect.width === 0) continue;
      fragments.push({ top: rect.top, bottom: rect.bottom, height: rect.height });
    }
  }
  fragments.sort((a, b) => a.top - b.top);
  const bottoms: number[] = [];
  let line: { top: number; bottom: number; height: number } | null = null;
  for (const f of fragments) {
    // Same-line fragments share a baseline, so their tops sit within a
    // fraction of a glyph box of each other; the next line starts at least
    // one line box lower. Half the smaller fragment height separates the two
    // even when an emoji's glyph box overhangs its line.
    if (line !== null && f.top - line.top < Math.min(f.height, line.height) / 2) {
      line.bottom = Math.max(line.bottom, f.bottom);
      bottoms[bottoms.length - 1] = line.bottom;
    } else {
      line = { ...f };
      bottoms.push(line.bottom);
    }
  }
  return bottoms;
}
